/*
 * Bitrise app picker: pure model, no React and nothing that touches `window`. Which account lists the apps, and the
 * hints under the field.
 */
import type { BitriseApp, ProviderAccount } from '@/api/schemasProvider';

type AccountLike = Pick<ProviderAccount, 'id' | 'kind' | 'name' | 'externalAccount'> & { status?: string };

/** The Bitrise accounts the org can still use (a disabled one cannot list its apps). */
export const bitriseAccountsOf = <A extends AccountLike>(accounts: A[] | undefined): A[] => (accounts ?? []).filter((a) => a.kind === 'bitrise' && a.status !== 'disabled');

/** The account the person picked, else the first one. */
export const pickAccount = <A extends Pick<AccountLike, 'id'>>(accounts: A[], accountId: string | undefined): A | undefined => accounts.find((a) => a.id === accountId) ?? accounts[0];

/** The workspace name shown in the hints: the Bitrise account's own name when known. */
export const accountLabel = (account: Pick<AccountLike, 'name' | 'externalAccount'>) => account.externalAccount ?? account.name;

/** Why the slug is typed instead of picked: no account to list from, no listing on this API, or the listing failed. */
export type SlugFallback = 'no-account' | 'unsupported' | 'failed';

const SLUG_HINT: Record<SlugFallback, string> = {
  'no-account': 'Optional · the app slug on bitrise.io (the app → Settings); connect a Bitrise account to pick it from a list',
  unsupported: 'Optional · the app slug on bitrise.io (the app → Settings)',
  failed: 'Optional · the Bitrise apps could not be listed; type the slug (bitrise.io → the app → Settings)',
};

export const slugFallbackHint = (why: SlugFallback) => SLUG_HINT[why];

/** Whether (and why) the field falls back to a typed slug; `undefined` when the apps can be listed. */
export function slugFallbackOf(hasAccount: boolean, unsupported: boolean, listFailed: boolean): SlugFallback | undefined {
  if (!hasAccount) return 'no-account';
  if (unsupported) return 'unsupported';
  return listFailed ? 'failed' : undefined;
}

/** Hint of the picker: the selected app's type and repository, or how many apps the workspace has. */
export function pickerHint(selected: Pick<BitriseApp, 'slug' | 'projectType' | 'repoUrl'> | undefined, count: number, workspace: string): string {
  if (selected) return [selected.projectType, selected.repoUrl].filter(Boolean).join(' · ') || selected.slug;
  return `Optional · ${count} ${count === 1 ? 'app' : 'apps'} in ${workspace}`;
}
